import React, { useState } from "react";
import Icon from "@/components/Icon";

// Reusable universal social-media selector. Used across landing pages, vCard,
// business page, social hub, feedback thank-you, etc.
export const SOCIALS = [
  { id: "facebook", label: "Facebook", icon: "Facebook", color: "#1877F2" },
  { id: "instagram", label: "Instagram", icon: "Instagram", color: "#E4405F" },
  { id: "threads", label: "Threads", icon: "AtSign", color: "#000000" },
  { id: "tiktok", label: "TikTok", icon: "Music2", color: "#000000" },
  { id: "x", label: "X", icon: "Twitter", color: "#000000" },
  { id: "youtube", label: "YouTube", icon: "Youtube", color: "#FF0000" },
  { id: "vimeo", label: "Vimeo", icon: "Video", color: "#1AB7EA" },
  { id: "linkedin", label: "LinkedIn", icon: "Linkedin", color: "#0A66C2" },
  { id: "pinterest", label: "Pinterest", icon: "Image", color: "#BD081C" },
  { id: "snapchat", label: "Snapchat", icon: "Ghost", color: "#FFFC00" },
  { id: "whatsapp", label: "WhatsApp", icon: "MessageCircle", color: "#25D366" },
  { id: "telegram", label: "Telegram", icon: "Send", color: "#26A5E4" },
  { id: "messenger", label: "Messenger", icon: "MessageSquare", color: "#0084FF" },
  { id: "discord", label: "Discord", icon: "MessagesSquare", color: "#5865F2" },
  { id: "reddit", label: "Reddit", icon: "Rss", color: "#FF4500" },
  { id: "twitch", label: "Twitch", icon: "Twitch", color: "#9146FF" },
  { id: "spotify", label: "Spotify", icon: "Music", color: "#1DB954" },
  { id: "soundcloud", label: "SoundCloud", icon: "AudioLines", color: "#FF5500" },
  { id: "applemusic", label: "Apple Music", icon: "Music4", color: "#FA243C" },
  { id: "github", label: "GitHub", icon: "Github", color: "#181717" },
  { id: "behance", label: "Behance", icon: "Palette", color: "#1769FF" },
  { id: "dribbble", label: "Dribbble", icon: "Dribbble", color: "#EA4C89" },
  { id: "medium", label: "Medium", icon: "BookOpen", color: "#000000" },
  { id: "yelp", label: "Yelp", icon: "Star", color: "#FF1A1A" },
  { id: "tripadvisor", label: "TripAdvisor", icon: "MapPin", color: "#34E0A1" },
  { id: "google", label: "Google Business", icon: "Chrome", color: "#4285F4" },
  { id: "email", label: "Email", icon: "Mail", color: "#6366F1" },
  { id: "phone", label: "Phone", icon: "Phone", color: "#10B981" },
  { id: "website", label: "Website", icon: "Globe", color: "#0EA5E9" },
  { id: "custom", label: "Custom link", icon: "Link", color: "#64748B" },
];
export const socialMeta = (id) => SOCIALS.find((s) => s.id === id) || SOCIALS.find((s) => s.id === "custom");

export const DEFAULT_SOCIAL_STYLE = { shape: "circle", size: 40, color: "brand", mono: "#6366F1" };

export function SocialLinks({ links = [], style, testidPrefix = "social" }) {
  const st = { ...DEFAULT_SOCIAL_STYLE, ...(style || {}) };
  const items = (links || []).filter((s) => s.url);
  if (!items.length) return null;
  const radius = st.shape === "circle" ? "9999px" : st.shape === "rounded" ? "12px" : "0px";
  return (
    <div className="flex flex-wrap justify-center gap-3 pt-2">
      {items.map((s, i) => {
        const m = socialMeta(s.platform);
        const href = s.platform === "whatsapp" && !String(s.url).startsWith("http") ? `https://wa.me/${String(s.url).replace(/[^0-9]/g, "")}` : s.url;
        let bg = m.color, fg = "#fff", border = "none";
        if (st.color === "mono") bg = st.mono;
        else if (st.color === "outline") { bg = "transparent"; fg = m.color; border = `2px solid ${m.color}`; }
        else if (st.color === "bare") { bg = "transparent"; fg = m.color; }
        return (
          <a key={i} data-testid={`${testidPrefix}-${i}`} href={href} target="_blank" rel="noreferrer"
            className="grid place-items-center transition-transform hover:scale-110"
            style={{ width: st.size, height: st.size, background: bg, color: fg, border, borderRadius: radius }}>
            <Icon name={m.icon} style={{ width: st.size * 0.45, height: st.size * 0.45 }} />
          </a>
        );
      })}
    </div>
  );
}

export default function SocialSelector({ value = [], onChange, style, onStyle }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const add = (id) => { onChange([...(value || []), { platform: id, url: "" }]); setOpen(false); setSearch(""); };
  const upd = (i, url) => { const c = [...value]; c[i] = { ...c[i], url }; onChange(c); };
  const move = (i, dir) => { const c = [...value]; const j = i + dir; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const filtered = SOCIALS.filter((s) => s.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Social links</span>
        <button type="button" data-testid="social-add-open" onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-primary flex items-center gap-1"><Icon name="Plus" className="h-3.5 w-3.5" />Add social</button>
      </div>

      {open && (
        <div className="rounded-xl border border-border bg-card p-3">
          <input autoFocus data-testid="social-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search networks..." className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm mb-2" />
          <div className="grid grid-cols-5 gap-2 max-h-52 overflow-y-auto no-scrollbar">
            {filtered.map((s) => (
              <button type="button" key={s.id} data-testid={`social-pick-${s.id}`} onClick={() => add(s.id)} title={s.label}
                className="flex flex-col items-center gap-1 rounded-lg border border-border p-2 hover:bg-accent transition-colors">
                <span className="h-8 w-8 rounded-full grid place-items-center text-white" style={{ background: s.color }}><Icon name={s.icon} className="h-4 w-4" /></span>
                <span className="text-[10px] truncate w-full text-center">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(value || []).map((item, i) => {
          const m = socialMeta(item.platform);
          return (
            <div key={i} data-testid={`social-item-${i}`} className="flex items-center gap-2">
              <span className="h-9 w-9 shrink-0 rounded-lg grid place-items-center text-white" style={{ background: m.color }}><Icon name={m.icon} className="h-4 w-4" /></span>
              <input value={item.url} onChange={(e) => upd(i, e.target.value)} placeholder={`${m.label} URL`} data-testid={`social-url-${i}`} className="flex-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
              <button type="button" onClick={() => move(i, -1)} className="h-8 w-8 grid place-items-center rounded-lg border border-border hover:bg-accent"><Icon name="ChevronUp" className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} className="h-8 w-8 grid place-items-center rounded-lg border border-border hover:bg-accent"><Icon name="ChevronDown" className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => remove(i)} data-testid={`social-remove-${i}`} className="h-8 w-8 grid place-items-center rounded-lg border border-border hover:text-destructive"><Icon name="Trash2" className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
        {!(value || []).length && !open && <p className="text-xs text-muted-foreground">No social links yet. Click "Add social".</p>}
      </div>

      {onStyle && (value || []).length > 0 && (
        <div className="rounded-xl border border-border bg-card p-3 space-y-2.5" data-testid="social-style-controls">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Icon style</div>
          <div className="flex flex-wrap gap-1.5">
            {["circle", "rounded", "square", "bare"].map((sh) => <button key={sh} type="button" data-testid={`social-shape-${sh}`} onClick={() => onStyle({ ...{ shape: "circle", size: 40, color: "brand", mono: "#6366F1" }, ...(style || {}), shape: sh })} className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${(style?.shape || "circle") === sh ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{sh}</button>)}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[["brand", "Brand colors"], ["mono", "Single color"], ["outline", "Outline"], ["bare", "Icon only"]].map(([c, l]) => <button key={c} type="button" data-testid={`social-color-${c}`} onClick={() => onStyle({ ...{ shape: "circle", size: 40, color: "brand", mono: "#6366F1" }, ...(style || {}), color: c })} className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${(style?.color || "brand") === c ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{l}</button>)}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs">Size {style?.size || 40}px</span>
            <input type="range" min="28" max="64" value={style?.size || 40} data-testid="social-size" onChange={(e) => onStyle({ ...{ shape: "circle", size: 40, color: "brand", mono: "#6366F1" }, ...(style || {}), size: +e.target.value })} className="flex-1 accent-[hsl(var(--primary))]" />
            {(style?.color || "brand") === "mono" && <input type="color" value={style?.mono || "#6366F1"} onChange={(e) => onStyle({ ...(style || {}), color: "mono", mono: e.target.value })} className="h-7 w-9 rounded border border-border" />}
          </div>
        </div>
      )}
    </div>
  );
}
